import { type UseQueryResult } from '@tanstack/react-query';
import { StyleSheet, css } from 'aphrodite';
import { type FC, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { type ContactFilter, ContactFilterCriteriaProperty } from '../../../extViewmodels';
import { useLeadFilterByCompanyQuery } from '../../../queries/Lead/useLeadFilterByCompanyQuery';
import { useLeadFilterByContactQuery } from '../../../queries/Lead/useLeadFilterByContactQuery';
import { useLeadSearchQuery } from '../../../queries/Lead/useLeadSearchQuery';
import { Button } from '../../../web/components-v2/Button';
import { Input } from '../../../web/components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import { CompanyFilterCriteriaProperty, type ICompanyFilter } from '../../viewModels/companyfilter';
import type { ILeadSearchView } from '../../viewModels/leads/interfaces';
import { LeadServedSource } from '../../viewModels/queue';
import { IconSearch } from '../ui/icons';
import { SearchResultRow } from './SearchResultRow';

/**
 * Drawer-tab global search affordance for the focused view. Mounts inside
 * `<AppDrawerContent>` alongside Wrap-up / Pin Config / Settings, toggled
 * by the standard `AppDrawerToolbarButton` toggle-group pattern.
 *
 * Mirrors the legacy `aida/components/SearchModal` data flow — same
 * react-query hooks — minus the MobX `observer` plumbing the legacy modal
 * threaded through. The legacy `ISearchPayload` flow is replaced by the
 * explicit `rescueMode` prop set by `FocusedLeadView` when an inbound call
 * connects with no `companyId` (CallFromUnknown).
 *
 * Originally rendered each hit with the shared `<LeadCard>`/`<CardView>`,
 * but that layout is built for a 600+ px wide horizontal slot and
 * overflowed inside the 400px-wide drawer tab. Each result is now a
 * compact `<SearchResultRow>` instead, and `SearchPanel` owns
 * navigation directly: `history.push` to `/focused-view?from=Search&...`,
 * then fire `onResultSelected` so the parent can close the drawer.
 */
export type SearchPanelProps = {
	/** Optional initial query string. Defaults to '' (fresh on each open). */
	initialQuery?: string;
	/**
	 * Called when a search result is clicked. Receives the picked `companyId`
	 * so the parent can sync its in-memory lead state even when the URL push
	 * was a no-op (HashHistory rejects duplicate paths — `history.push` to the
	 * same `?companyId=X` does nothing and `location.key` never changes, which
	 * otherwise leaves the focused view stuck on its previous state).
	 */
	onResultSelected?: (companyId: string) => void;
	/**
	 * Rescue mode (legacy: `SearchKeyOptions.CallFromUnknown`). When true the
	 * panel renders a banner explaining the unknown caller, defaults the
	 * search type to `phone`, pre-arms the query with `initialQuery`, and
	 * routes selection through `onRescueAttach` instead of the standard
	 * navigation handler.
	 */
	rescueMode?: boolean;
	/** E.164 of the unknown inbound caller. Surfaced in the banner copy. */
	rescuePhone?: string;
	/**
	 * Invoked when the rep selects a result while in rescue mode. The parent
	 * is responsible for the actual attach (`useAttachCallToLead` mutation +
	 * navigation + drawer dismiss). Falls back to the standard navigation
	 * handler when not provided.
	 */
	onRescueAttach?: (companyId: string) => void;
};

type SearchType = 'all' | 'contact' | 'company' | 'phone' | 'email';

const SEARCH_TYPES: ReadonlyArray<{ value: SearchType; label: string }> = [
	{ value: 'all', label: 'All' },
	{ value: 'contact', label: 'Contact' },
	{ value: 'company', label: 'Company' },
	{ value: 'phone', label: 'Phone' },
	{ value: 'email', label: 'Email' },
];

export const SearchPanel: FC<SearchPanelProps> = ({
	initialQuery = '',
	onResultSelected,
	rescueMode = false,
	rescuePhone,
	onRescueAttach,
}) => {
	const history = useHistory();
	const inputRef = useRef<HTMLInputElement>(null);
	const [searchValue, setSearchValue] = useState(initialQuery);
	// Default to `phone` in rescue mode — the inbound number is the only
	// signal the rep has, and a phone-typed search avoids stray contact-name
	// matches that wouldn't resolve the caller.
	const [searchType, setSearchType] = useState<SearchType>(rescueMode ? 'phone' : 'all');
	// Only set after the user explicitly hits Search/Enter — keeps us from
	// firing a query on every keystroke. Rescue mode pre-arms the active
	// query with the inbound phone so results land on first paint.
	const [activeQuery, setActiveQuery] = useState(rescueMode ? initialQuery : '');
	const [activeType, setActiveType] = useState<SearchType>(rescueMode ? 'phone' : 'all');

	// Re-sync local state if the initial query / rescue context flips while
	// the panel is mounted — happens when the drawer auto-opens for a new
	// inbound call (legacy: `SearchModal`'s effect on `initialQuery`).
	useEffect(() => {
		if (rescueMode) {
			setSearchValue(initialQuery);
			setActiveQuery(initialQuery);
			setSearchType('phone');
			setActiveType('phone');
		}
	}, [rescueMode, initialQuery]);

	// Auto-focus the input on mount. The drawer-tab pattern unmounts the
	// panel when switched away (`unmountOnExit`), so each toggle-on remounts
	// and re-fires this effect — the rep can immediately type.
	useEffect(() => {
		const handle = window.setTimeout(() => inputRef.current?.focus(), 50);
		return () => window.clearTimeout(handle);
	}, []);

	let contactFilter: ContactFilter | null = null;
	let companyFilter: ICompanyFilter | null = null;
	if (activeQuery) {
		switch (activeType) {
			case 'contact': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.NamePrefix,
							value: activeQuery,
						},
					],
				};
				break;
			}
			case 'company': {
				companyFilter = {
					criteria: [
						{
							property: CompanyFilterCriteriaProperty.NamePrefix,
							value: activeQuery,
						},
					],
				};
				break;
			}
			case 'phone': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.PhoneNumber,
							value: activeQuery,
						},
					],
				};
				companyFilter = {
					criteria: [
						{
							property: CompanyFilterCriteriaProperty.PhoneNumber,
							value: activeQuery,
						},
					],
				};
				break;
			}
			case 'email': {
				contactFilter = {
					criteria: [
						{
							property: ContactFilterCriteriaProperty.Email,
							value: activeQuery,
						},
					],
				};
				break;
			}
			default: {
				break;
			}
		}
	}

	const leadSearchQuery = useLeadSearchQuery({
		enabled: activeType === 'all' && Boolean(activeQuery),
		search: activeQuery,
		refetchOnWindowFocus: false,
	});
	const leadFilterByContactQuery = useLeadFilterByContactQuery({
		enabled: Boolean(contactFilter),
		filter: contactFilter,
		meta: {
			toastErrorMessage: 'Error fetching contact leads',
			errorLogging: { category: 'FocusedViewSearchPanel', action: 'FocusedViewSearchLeadFilterByContactQuery-Error' },
		},
		swallowHttpErrors: true,
		refetchOnWindowFocus: false,
	});
	const leadFilterByCompanyQuery = useLeadFilterByCompanyQuery({
		enabled: Boolean(companyFilter),
		filter: companyFilter,
		meta: {
			toastErrorMessage: 'Error fetching company leads',
			errorLogging: { category: 'FocusedViewSearchPanel', action: 'FocusedViewSearchLeadFilterByCompanyQuery-Error' },
		},
		swallowHttpErrors: true,
		refetchOnWindowFocus: false,
	});

	const activeQueries: UseQueryResult<ILeadSearchView[], Error>[] = [];
	if (activeType === 'all' && Boolean(activeQuery)) {
		activeQueries.push(leadSearchQuery);
	}
	if (contactFilter) {
		activeQueries.push(leadFilterByContactQuery);
	}
	if (companyFilter) {
		activeQueries.push(leadFilterByCompanyQuery);
	}

	const handleSubmit = () => {
		const trimmed = searchValue.trim();
		if (!trimmed) return;
		setActiveQuery(trimmed);
		setActiveType(searchType);
	};

	const handleResultClick = (lead: ILeadSearchView) => {
		const companyId = lead.company?.id;
		if (!companyId) return;
		// Rescue mode: hand the selection back to the parent so it can run the
		// `useAttachCallToLead` mutation (cache patch + lead/contacts
		// invalidation) before navigating. Without that hop, the search
		// result would route the rep to the new lead but the active inbound
		// call would still read as "no companyId" until wrap-up saved.
		if (rescueMode && onRescueAttach) {
			onRescueAttach(companyId);
			onResultSelected?.(companyId);
			return;
		}
		history.push({
			pathname: '/focused-view',
			search: `?from=${LeadServedSource.Search}&companyId=${companyId}`,
		});
		onResultSelected?.(companyId);
	};

	const renderResults = () => {
		if (!activeQueries.length) {
			return <div className={css(styles.empty)}>Enter a search term and hit Search.</div>;
		}
		if (activeQueries.some(q => q.isLoading)) {
			return (
				<div className={css(styles.spinnerRow)}>
					<LoadingSpinner type='large' />
				</div>
			);
		}
		if (!activeQueries.every(q => q.isSuccess)) {
			return null;
		}
		if (activeQueries.every(q => !q.data?.length)) {
			return <div className={css(styles.empty)}>{`No results for "${activeQuery}"`}</div>;
		}
		return (
			<div className={css(styles.list)}>
				{activeQueries
					.flatMap(q => q.data ?? [])
					.map(lead => (
						<SearchResultRow
							key={`focused-view-search-${lead.company?.id ?? 'unknown'}-${lead.contactReferences?.[0]?.id ?? 'none'}`}
							lead={lead}
							onClick={() => handleResultClick(lead)}
						/>
					))}
			</div>
		);
	};

	return (
		<div className={css(styles.root)}>
			<header className={css(styles.head)}>
				<span className={css(styles.title)}>SEARCH</span>
			</header>

			{rescueMode && (
				<div role='alert' aria-live='polite' className={css(styles.rescueBanner)}>
					<span className={css(styles.rescueBannerTitle)}>Unrecognized incoming call</span>
					<span className={css(styles.rescueBannerBody)}>
						{rescuePhone
							? `We don't recognize ${rescuePhone} — search for the company to attach this call.`
							: "We don't recognize this number — search for the company to attach this call."}
					</span>
				</div>
			)}

			<form
				className={css(styles.controls)}
				onSubmit={e => {
					e.preventDefault();
					handleSubmit();
				}}
			>
				<div className={css(styles.typeSelect)}>
					<Select value={searchType} onValueChange={(v: string) => setSearchType(v as SearchType)}>
						<SelectTrigger>
							<SelectValue>{SEARCH_TYPES.find(t => t.value === searchType)?.label ?? 'All'}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{SEARCH_TYPES.map(t => (
								<SelectItem key={t.value} value={t.value}>
									{t.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className={css(styles.inputWrap)}>
					<span className={css(styles.inputIcon)} aria-hidden='true'>
						<IconSearch size={16} />
					</span>
					<Input
						ref={inputRef}
						styles={[styles.input]}
						value={searchValue}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
						placeholder='Search leads...'
						name='search'
					/>
				</div>
				<Button type='submit' variant='primary'>
					Search
				</Button>
			</form>

			<div className={css(styles.results)}>{renderResults()}</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		// `--bg-elev` matches sibling drawer tabs (PinConfigPanel, WrapupPanel,
		// SettingsPanel) so all tabs in the SideDrawer read as the same
		// elevation surface.
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		padding: '14px 18px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	title: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	// Amber alert surface for the CallFromUnknown rescue banner. Uses the
	// pin-orange accent token (already used by the Game Plan card) so the
	// banner reads as "important context" without introducing a new color
	// scale just for this surface. components-v2 has no Banner / Alert
	// primitive yet (verified in components-v2/ at prompt time) — this is a
	// local inline alert pattern, kept tight to the panel.
	rescueBanner: {
		display: 'flex',
		flexDirection: 'column',
		gap: 4,
		padding: '12px 18px',
		borderBottom: '1px solid var(--border)',
		background: 'var(--pin-orange-soft, rgba(245, 158, 11, 0.12))',
		borderLeft: '3px solid var(--pin-orange, #f59e0b)',
		flexShrink: 0,
	},
	rescueBannerTitle: {
		fontSize: 12,
		fontWeight: 600,
		color: 'var(--pin-orange, #b45309)',
		textTransform: 'uppercase',
		letterSpacing: 0.4,
	},
	rescueBannerBody: {
		fontSize: 13,
		color: 'var(--ink)',
		lineHeight: 1.4,
	},
	controls: {
		display: 'flex',
		gap: 8,
		alignItems: 'stretch',
		padding: '14px 18px',
		flexShrink: 0,
	},
	typeSelect: {
		flex: '0 0 120px',
	},
	inputWrap: {
		flex: 1,
		minWidth: 0,
		position: 'relative',
		display: 'flex',
		alignItems: 'center',
	},
	inputIcon: {
		position: 'absolute',
		left: 10,
		display: 'inline-flex',
		alignItems: 'center',
		pointerEvents: 'none',
		color: 'var(--ink-muted, #6b7280)',
	},
	input: {
		paddingLeft: 32,
		width: '100%',
	},
	results: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: '0 18px 18px',
	},
	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: 8,
	},
	spinnerRow: {
		display: 'flex',
		justifyContent: 'center',
		padding: 32,
	},
	empty: {
		padding: 32,
		textAlign: 'center',
		color: 'var(--ink-muted, #6b7280)',
	},
});
